// Interface pour les statistiques globales
export interface GlobalStats {
  totalCompanies: number;
  totalUsers: number;
  totalAdmins: number;
  totalEmployees: number;
  totalSessions: number; // Total depuis le début (pour référence)
  sessionsThisMonth: number; // Sessions de ce mois (affiché dans le dashboard)
  totalSessionsToday: number;
  activeCompanies: number;
  companiesChange: number;
  usersChange: number;
  sessionsChange: number;
  sessionsTodayChange: number;
}

import { AxiosError } from "axios";
import { api } from "./api";

// Interface pour la réponse de l'API
interface GlobalStatsResponse {
  message: string;
  stats: GlobalStats;
}

// Fonction pour récupérer les stats globales
export const getGlobalStats = async (): Promise<GlobalStats> => {
  try {
    const response = await api.get<GlobalStatsResponse>("/stats/global");
    // L'API retourne { message, stats }, on retourne seulement stats
    return response.data.stats;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la récupération des statistiques globales"
      );
    }
    throw new Error("Erreur lors de la récupération des statistiques globales");
  }
};

// Interface pour les statistiques d'entreprise (ADMIN)
export interface CompanyStats {
  totalEmployees: number;
  activeEmployees: number;
  totalSessionsToday: number;
  sessionsThisMonth: number;
  totalHoursThisMonth: number;
  averageDailyHours: number;
  averageSessionDuration: number;
  topPerformingEmployees: {
    name: string;
    hours: number;
  }[];
  employeesChange: number;
  sessionsChange: number;
  hoursChange: number;
  sessionsTodayChange: number;
  activeSessions: number;
  activeSessionsEmployees: {
    name: string;
  }[];
  period: string; // "day", "week", ou "month"
  sessionsInPeriod: number;
  hoursInPeriod: number;
  periodLabel: string; // "aujourd'hui", "cette semaine", "ce mois"
  comparisonLabel: string; // "hier", "semaine dernière", "mois dernier"
}

// Interface pour la réponse de l'API
interface CompanyStatsResponse {
  message: string;
  stats: CompanyStats;
}

// Stats d'une entreprise (pour ADMIN)
// Note: companyId n'est pas utilisé car l'endpoint utilise celui de l'utilisateur connecté
export const getCompanyStats = async (
  _companyId: string,
  period?: string
): Promise<CompanyStats> => {
  try {
    const periodParam = period ? `?period=${period}` : "";
    const url = `/stats/company${periodParam}`;
    const response = await api.get<CompanyStatsResponse>(url);
    return response.data.stats;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        (error.response?.data as { message?: string } | undefined)?.message ||
          "Erreur lors de la récupération des statistiques d'entreprise"
      );
    }
    throw new Error(
      "Erreur lors de la récupération des statistiques d'entreprise"
    );
  }
};

// Interface pour les données hebdomadaires
export interface WeeklyStatsData {
  date: string;
  day: string;
  count: number;
}

// Interface pour les données mensuelles
export interface MonthlyStatsData {
  month: string;
  monthShort: string;
  count: number;
}

// Interface pour les données quotidiennes du mois
export interface MonthlyDailyStatsData {
  date?: string;
  label: string; // Label pour l'axe X (ex: "1", "Lun", "8h")
  day: string;
  dayName?: string;
  count: number;
}

// Interface pour la réponse de l'API
interface StatsResponse {
  message: string;
  data: WeeklyStatsData[] | MonthlyStatsData[] | MonthlyDailyStatsData[];
}

// Fonction pour récupérer les stats hebdomadaires
export const getWeeklyStats = async (
  companyId?: string
): Promise<WeeklyStatsData[]> => {
  try {
    // Construire l'URL avec le query param seulement si companyId existe
    const url = companyId
      ? `/stats/weekly?companyId=${companyId}`
      : "/stats/weekly";

    const response = await api.get<StatsResponse>(url);
    // L'API retourne { message, data }, on retourne seulement data
    return response.data.data as WeeklyStatsData[];
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la récupération des données hebdomadaires"
      );
    }
    throw new Error("Erreur lors de la récupération des données hebdomadaires");
  }
};

// Fonction pour récupérer les stats mensuelles
export const getMonthlyStats = async (
  companyId?: string
): Promise<MonthlyStatsData[]> => {
  try {
    // Construire l'URL avec le query param seulement si companyId existe
    const url = companyId
      ? `/stats/monthly?companyId=${companyId}`
      : "/stats/monthly";

    const response = await api.get<StatsResponse>(url);
    // L'API retourne { message, data }, on retourne seulement data
    return response.data.data as MonthlyStatsData[];
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la récupération des données mensuelles"
      );
    }
    throw new Error("Erreur lors de la récupération des données mensuelles");
  }
};

// ============================================================
// Types pour les heures par employé
// ============================================================

export interface EmployeeHoursItem {
  employee: { id: string; name: string };
  minutes: number;
  sessionsCount: number;
}

export interface EmployeeHoursData {
  employees: EmployeeHoursItem[];
  totalMinutes: number;
  label: string;
  period: string;
}

interface EmployeeHoursResponse {
  message: string;
  data: EmployeeHoursData;
}

/**
 * Récupère les heures travaillées par employé pour une période donnée.
 * @param period   "day" | "week" | "month"
 * @param refDate  La date de référence (ex: new Date())
 */
export const getEmployeeHours = async (
  period: string,
  refDate: Date
): Promise<EmployeeHoursData> => {
  try {
    // On envoie la date en ISO string (ex: "2026-05-06T00:00:00.000Z")
    const refDateParam = refDate.toISOString().split("T")[0]; // "2026-05-06"
    const url = `/stats/employee-hours?period=${period}&refDate=${refDateParam}`;
    const response = await api.get<EmployeeHoursResponse>(url);
    return response.data.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        (error.response?.data as { message?: string } | undefined)?.message ||
          "Erreur lors de la récupération des heures par employé"
      );
    }
    throw new Error("Erreur lors de la récupération des heures par employé");
  }
};

// Fonction pour récupérer les stats quotidiennes du mois en cours
export const getMonthlyDailyStats = async (
  period?: string,
  companyId?: string
): Promise<MonthlyDailyStatsData[]> => {
  try {
    const periodParam = period ? `period=${period}` : "";
    const companyParam = companyId ? `companyId=${companyId}` : "";
    const params = [periodParam, companyParam].filter(Boolean).join("&");
    const url = `/stats/monthly-daily${params ? `?${params}` : ""}`;

    const response = await api.get<StatsResponse>(url);
    return response.data.data as MonthlyDailyStatsData[];
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        (error.response?.data as { message?: string } | undefined)?.message ||
          "Erreur lors de la récupération des données quotidiennes du mois"
      );
    }
    throw new Error(
      "Erreur lors de la récupération des données quotidiennes du mois"
    );
  }
};
